import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
    ArrowLeftIcon,
    ShieldExclamationIcon,
    UserCircleIcon,
    CalendarIcon,
    ExclamationTriangleIcon,
    DocumentTextIcon,
    LinkIcon,
    ClockIcon,
} from "@heroicons/react/24/outline";

import { Button } from "@/components/ui/Button";
import { SECTION_PERMISSIONS } from "@/config/permissions";
import { usePermissions } from "@/features/auth/usePermissions";
import { getFraudAlertDetails, updateAlertStatus } from "../api/fraudSafetyApi";
import { extractApiError } from "@/lib/api-error";
import type { FraudAlertStatus } from "../types";

// ============================================================================
// Badges
// ============================================================================

const SeverityBadge = ({ severity }: { severity: string }) => {
    const s = severity ?? "UNKNOWN";
    const styles: Record<string, string> = {
        CRITICAL: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400",
        HIGH: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
        MEDIUM: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
        LOW: "bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400",
    };
    return (
        <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium ${styles[s] || "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"}`}>
            <span className={`h-2 w-2 rounded-full ${s === "CRITICAL" ? "bg-rose-500" : s === "HIGH" ? "bg-orange-500" : s === "MEDIUM" ? "bg-amber-500" : s === "LOW" ? "bg-sky-500" : "bg-slate-400"}`} />
            {s}
        </span>
    );
};

const StatusBadge = ({ status }: { status: string }) => {
    const s = status ?? "UNKNOWN";
    const styles: Record<string, string> = {
        NEW: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
        UNDER_REVIEW: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
        RESOLVED: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
        FALSE_POSITIVE: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400",
        ESCALATED: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400",
    };
    return (
        <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium ${styles[s] || "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"}`}>
            <span className={`h-2 w-2 rounded-full ${s === "NEW" ? "bg-blue-500" : s === "UNDER_REVIEW" ? "bg-amber-500" : s === "RESOLVED" ? "bg-emerald-500" : s === "FALSE_POSITIVE" ? "bg-slate-400" : s === "ESCALATED" ? "bg-rose-500" : "bg-slate-400"}`} />
            {s.replace("_", " ")}
        </span>
    );
};

// ============================================================================
// Helpers
// ============================================================================

const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return "—";
    try {
        return new Intl.DateTimeFormat("en-NG", {
            month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit",
        }).format(new Date(dateStr));
    } catch {
        return dateStr;
    }
};

const InfoRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div className="flex items-start justify-between py-2.5">
        <span className="text-sm text-slate-500 dark:text-slate-400">{label}</span>
        <span className="text-right text-sm font-medium text-slate-900 dark:text-white">{children}</span>
    </div>
);

// ============================================================================
// Modal
// ============================================================================

type ModalProps = { open: boolean; title: string; onClose: () => void; children: React.ReactNode };
const Modal = ({ open, title, onClose, children }: ModalProps) => {
    if (!open) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50" onClick={onClose} />
            <div className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl dark:bg-slate-900">
                <h3 className="mb-4 text-lg font-semibold text-slate-900 dark:text-white">{title}</h3>
                {children}
            </div>
        </div>
    );
};

// ============================================================================
// Status options for update modal
// ============================================================================

const statusOptions: { value: FraudAlertStatus; label: string }[] = [
    { value: "NEW", label: "New" },
    { value: "UNDER_REVIEW", label: "Under Review" },
    { value: "RESOLVED", label: "Resolved" },
    { value: "FALSE_POSITIVE", label: "False Positive" },
    { value: "ESCALATED", label: "Escalated" },
];

// ============================================================================
// Component
// ============================================================================

export const AlertDetailsRoute = () => {
    const { alertId } = useParams<{ alertId: string }>();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { hasPermission } = usePermissions();

    const [statusModal, setStatusModal] = useState(false);
    const [newStatus, setNewStatus] = useState<FraudAlertStatus>("UNDER_REVIEW");
    const [notes, setNotes] = useState("");
    const [actionError, setActionError] = useState("");

    const { data, isLoading, error } = useQuery({
        queryKey: ["fraud-alert-details", alertId],
        queryFn: () => getFraudAlertDetails(alertId!),
        enabled: !!alertId,
    });

    const alert = data?.data;
    const canManageFraud = hasPermission(SECTION_PERMISSIONS.FRAUD_MANAGE[0]);

    const statusMutation = useMutation({
        mutationFn: () => updateAlertStatus(alertId!, { status: newStatus, notes }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["fraud-alert-details", alertId] });
            queryClient.invalidateQueries({ queryKey: ["fraud-alerts"] });
            setStatusModal(false);
            setNotes("");
            setActionError("");
        },
        onError: (err) => setActionError(extractApiError(err)),
    });

    // ========================================================================
    // Loading / Error
    // ========================================================================

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-20">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
            </div>
        );
    }

    if (error || !alert) {
        return (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 p-8 text-center dark:border-rose-800 dark:bg-rose-900/20">
                <ExclamationTriangleIcon className="mx-auto h-10 w-10 text-rose-400" />
                <h3 className="mt-3 text-lg font-semibold text-rose-700 dark:text-rose-300">Alert not found</h3>
                <Button variant="secondary" size="sm" className="mt-4" onClick={() => navigate("/fraud-safety/alerts")}>
                    Back to Alerts
                </Button>
            </div>
        );
    }

    // ========================================================================
    // Evidence JSON parse
    // ========================================================================

    let evidenceData: Record<string, unknown> | null = null;
    if (alert.evidenceJson) {
        try {
            evidenceData = JSON.parse(alert.evidenceJson);
        } catch {
            evidenceData = null;
        }
    }

    // ========================================================================
    // Related links
    // ========================================================================

    const relatedLinks = [
        { label: "Booking", id: alert.relatedBookingId, path: `/ride-bookings-trips/bookings/${alert.relatedBookingId}` },
        { label: "Ride Offer", id: alert.relatedRideOfferId, path: `/ride-bookings-trips/rides/${alert.relatedRideOfferId}` },
        { label: "Trip", id: alert.relatedTripId, path: `/ride-bookings-trips/trips/${alert.relatedTripId}` },
        { label: "Payment", id: alert.relatedPaymentId, path: `/wallet-payments/payments/${alert.relatedPaymentId}` },
        { label: "Transaction", id: alert.relatedTransactionId, path: `/wallet-payments/transactions/${alert.relatedTransactionId}` },
    ].filter((l) => l.id);

    // ========================================================================
    // Render
    // ========================================================================

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex items-start gap-4">
                    <button onClick={() => navigate("/fraud-safety/alerts")} className="mt-1 rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-300">
                        <ArrowLeftIcon className="h-5 w-5" />
                    </button>
                    <div>
                        <div className="flex items-center gap-3">
                            <ShieldExclamationIcon className="h-7 w-7 text-rose-500" />
                            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">{alert.title}</h1>
                        </div>
                        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{alert.description}</p>
                        <div className="mt-2 flex items-center gap-2">
                            <SeverityBadge severity={alert.severity} />
                            <StatusBadge status={alert.status} />
                        </div>
                    </div>
                </div>
                {canManageFraud ? (
                    <Button variant="primary" size="sm" onClick={() => { setNewStatus(alert.status); setStatusModal(true); }}>
                        Update Status
                    </Button>
                ) : null}
            </div>

            <div className="grid gap-6 lg:grid-cols-2">
                {/* Alert Info */}
                <div className="rounded-2xl border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
                    <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-white">Alert Information</h2>
                    <div className="divide-y divide-slate-100 dark:divide-slate-800">
                        <InfoRow label="Alert ID">{alert.alertId}</InfoRow>
                        <InfoRow label="Type">
                            <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-700 dark:bg-slate-800 dark:text-slate-300">{alert.alertType?.replace(/_/g, " ")}</span>
                        </InfoRow>
                        <InfoRow label="Detection Source">{alert.detectionSource}</InfoRow>
                        <InfoRow label="Detection Rule">{alert.detectionRule || "—"}</InfoRow>
                        <InfoRow label="Risk Score">
                            <span className={`text-lg font-bold ${alert.riskScore >= 70 ? "text-rose-600" : alert.riskScore >= 50 ? "text-orange-600" : alert.riskScore >= 30 ? "text-amber-600" : "text-emerald-600"}`}>
                                {alert.riskScore}
                            </span>
                        </InfoRow>
                        <InfoRow label="Confidence Score">{alert.confidenceScore != null ? `${alert.confidenceScore}%` : "—"}</InfoRow>
                        <InfoRow label="Created">{formatDate(alert.createdAt)}</InfoRow>
                        <InfoRow label="Updated">{formatDate(alert.updatedAt)}</InfoRow>
                    </div>
                </div>

                {/* User Info */}
                <div className="rounded-2xl border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
                    <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-white">User Information</h2>
                    <div className="divide-y divide-slate-100 dark:divide-slate-800">
                        <InfoRow label="Name">
                            <div className="flex items-center gap-2">
                                <UserCircleIcon className="h-4 w-4 text-slate-400" />
                                {alert.userName}
                            </div>
                        </InfoRow>
                        <InfoRow label="Email">{alert.userEmail}</InfoRow>
                        <InfoRow label="Type">
                            <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${alert.userType === "RIDER" ? "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400" : "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400"}`}>
                                {alert.userType}
                            </span>
                        </InfoRow>
                        <InfoRow label="User ID">
                            <Link to={alert.userType === "RIDER" ? `/riders/${alert.userId}` : `/drivers/${alert.userId}`} className="text-primary-600 hover:underline dark:text-primary-400">
                                {alert.userId.slice(0, 12)}…
                            </Link>
                        </InfoRow>
                    </div>

                    {/* Resolution */}
                    {(alert.resolvedAt || alert.resolvedBy || alert.resolutionNotes) && (
                        <>
                            <h3 className="mb-3 mt-6 text-base font-semibold text-slate-900 dark:text-white">Resolution</h3>
                            <div className="divide-y divide-slate-100 dark:divide-slate-800">
                                <InfoRow label="Resolved By">{alert.resolvedBy || "—"}</InfoRow>
                                <InfoRow label="Resolved At">{formatDate(alert.resolvedAt)}</InfoRow>
                                <InfoRow label="Notes">{alert.resolutionNotes || "—"}</InfoRow>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Related Links */}
            {relatedLinks.length > 0 && (
                <div className="rounded-2xl border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
                    <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-white">
                        <LinkIcon className="mr-2 inline h-5 w-5 text-slate-400" /> Related Records
                    </h2>
                    <div className="flex flex-wrap gap-3">
                        {relatedLinks.map((link) => (
                            <Link
                                key={link.label}
                                to={link.path}
                                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-primary-600 transition-colors hover:bg-slate-50 dark:border-slate-700 dark:text-primary-400 dark:hover:bg-slate-800"
                            >
                                <DocumentTextIcon className="h-4 w-4" />
                                {link.label}: {String(link.id).slice(0, 8)}…
                            </Link>
                        ))}
                    </div>
                </div>
            )}

            {/* Investigation Case */}
            {alert.investigationCaseId && (
                <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-900/20">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-sm text-amber-700 dark:text-amber-400">
                            <DocumentTextIcon className="h-4 w-4" />
                            Linked to Investigation Case
                        </div>
                        <Link
                            to={`/fraud-safety/cases/${alert.investigationCaseId}`}
                            className="text-sm font-medium text-amber-700 hover:underline dark:text-amber-400"
                        >
                            View Case →
                        </Link>
                    </div>
                </div>
            )}

            {/* Evidence JSON */}
            {alert.evidenceJson && (
                <div className="rounded-2xl border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
                    <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-white">Evidence Data</h2>
                    {evidenceData ? (
                        <pre className="max-h-96 overflow-auto rounded-xl bg-slate-50 p-4 text-xs text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                            {JSON.stringify(evidenceData, null, 2)}
                        </pre>
                    ) : (
                        <p className="text-sm text-slate-500">Raw: {alert.evidenceJson}</p>
                    )}
                </div>
            )}

            {/* Update Status Modal */}
            <Modal open={statusModal} title="Update Alert Status" onClose={() => setStatusModal(false)}>
                <div className="space-y-4">
                    <div>
                        <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">New Status</label>
                        <select
                            value={newStatus}
                            onChange={(e) => setNewStatus(e.target.value as FraudAlertStatus)}
                            className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                        >
                            {statusOptions.map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">Notes</label>
                        <textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            rows={3}
                            placeholder="Add resolution notes…"
                            className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                        />
                    </div>
                    {actionError && <p className="text-sm text-rose-600">{actionError}</p>}
                    <div className="flex justify-end gap-3">
                        <Button variant="secondary" size="sm" onClick={() => setStatusModal(false)}>Cancel</Button>
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={() => statusMutation.mutate()}
                            disabled={statusMutation.isPending || !notes.trim()}
                        >
                            {statusMutation.isPending ? "Updating…" : "Update Status"}
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};
